/** CurrentVersionCard — shows the DEPLOY TARGET version, not the main backend. */
import React from "react";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { Alert, AlertDescription } from "../ui/alert";
import { RefreshCw, GitCommit, Info, AlertTriangle } from "lucide-react";

export default function CurrentVersionCard({ status, loading, error, onRetry }) {
  // Error state takes precedence: never loop on a silent spinner if the
  // control plane is unreachable/unconfigured.
  if (error) {
    return (
      <Card className="p-6" data-testid="current-version-card-error">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="space-y-2">
            <div>
              Statut de la cible de déploiement indisponible.
            </div>
            <div className="text-xs opacity-80">{error}</div>
            {onRetry && (
              <button
                onClick={onRetry}
                className="text-xs underline mt-1"
                data-testid="retry-status-btn"
              >
                Réessayer
              </button>
            )}
          </AlertDescription>
        </Alert>
      </Card>
    );
  }
  if (!status) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 text-muted-foreground">
          <RefreshCw className="h-4 w-4 animate-spin" /> Chargement du statut cible…
        </div>
      </Card>
    );
  }
  const health = status.target_health || "unknown";
  const healthColor = health === "ok" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700";
  return (
    <div className="space-y-4">
      <Card className="p-6" data-testid="current-version-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm text-muted-foreground uppercase tracking-wide">Cible de déploiement</h3>
            <p className="text-2xl font-heading font-semibold text-primary mt-1" data-testid="current-version">
              {status.current_version || "—"}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Environnement Preview — cible : <code className="text-xs">/app/preview_deploy_target/itercatus</code> (isolée de l'app Preview elle-même)
            </p>
          </div>
          <Badge className={healthColor} data-testid="target-health">{health.toUpperCase()}</Badge>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-border text-sm">
          <div>
            <p className="text-muted-foreground">Version précédente (journal)</p>
            <p className="font-medium" data-testid="previous-version">{status.previous_version || "aucune"}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Endpoint /api/version</p>
            <p className="font-mono text-xs truncate">{status.target_version_endpoint || "—"}</p>
          </div>
        </div>
      </Card>
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription className="text-xs">
          Backend Updater lui-même : <code>dev-unmanaged</code> (informatif — non contrôlé par ce panneau).
        </AlertDescription>
      </Alert>
    </div>
  );
}
